import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import Grafico from 'src/componentes/chart';
import LoadingContainer from 'src/componentes/loading-container';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { getDataAplicacaoFormularioGlobal } from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import EvolucaoFiltroAplicacoes from './EvolucaoFiltroAplicacoes';
import EvolucaoFiltroChips from './EvolucaoFiltroChips';
import { findLikertRange, LIKERT_COLORS } from './likertHelpers';
import { SEGMENT_COLORS } from './npsHelpers';
import useDashboardEvolucao from './useDashboardEvolucao';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e4e7ec',
    borderRadius: 9,
    padding: '20px 22px',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    color: '#101828',
    fontSize: 18,
    fontWeight: 700,
  },
  empty: {
    color: '#98a2b3',
    padding: '48px 16px',
    textAlign: 'center',
  },
};

const obterValor = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const numero = Number(value);
  return Number.isFinite(numero) ? numero : null;
};

function EvolucaoAplicacoes({
  entidadeId,
  filtroEvolucao,
  filtros,
  refreshToken,
  tipo,
}) {
  const intl = useIntl();
  const { user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const { dados, isLoading } = useDashboardEvolucao(tipo, entidadeId, {
    enabled:
      !filtroEvolucao.periodoInvalido &&
      !filtroEvolucao.semAplicacoesSelecionadas,
    filtros,
    onDadosCarregados: filtroEvolucao.registrarResposta,
    parametrosEvolucao: filtroEvolucao.parametros,
    refreshToken,
  });
  const payload = dados?.model || dados;
  const lista = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.aplicacoes)
    ? payload.aplicacoes
    : Array.isArray(payload?.usos)
    ? payload.usos
    : [];
  const aplicacoes = lista
    .map((aplicacao) => {
      const data = getDataAplicacaoFormularioGlobal(aplicacao.dataInicio);
      return { ...aplicacao, data };
    })
    .filter(
      (aplicacao) => aplicacao.data && !Number.isNaN(aplicacao.data.getTime())
    )
    .sort((a, b) => a.data - b.data || a.formularioUsoId - b.formularioUsoId);
  const likert = tipo === 'likert';
  const series = likert
    ? [
        {
          label: intl.formatMessage({ id: 'label.geral' }),
          color: '#0D7E94',
          obter: (aplicacao) => aplicacao.mediaGeralPercentual,
        },
        ...['positiva', 'neutra', 'negativa'].map((codigo) => ({
          label: intl.formatMessage({
            id: {
              positiva: 'labelLikertPositiva',
              neutra: 'labelLikertNeutra',
              negativa: 'labelLikertNegativa',
            }[codigo],
          }),
          color: LIKERT_COLORS[codigo],
          obter: (aplicacao) =>
            findLikertRange(aplicacao.faixas, codigo)?.percentual,
        })),
      ]
    : [
        {
          label: intl.formatMessage({ id: 'label.geral' }),
          color: '#0D7E94',
          obter: (aplicacao) => aplicacao.npsScore,
        },
        ...['promotores', 'neutros', 'detratores'].map((codigo) => ({
          label: intl.formatMessage({
            id: {
              promotores: 'labelPromotores',
              neutros: 'labelNeutros',
              detratores: 'labelDetratores',
            }[codigo],
          }),
          color: SEGMENT_COLORS[codigo],
          percentual: true,
          obter: (aplicacao) => aplicacao[codigo]?.percentual,
        })),
      ];

  const chartConfig = {
    type: 'msline',
    width: '100%',
    height: 320,
    dataFormat: 'json',
    dataSource: {
      chart: {
        theme: 'fusion',
        bgAlpha: '0',
        canvasBgAlpha: '0',
        connectNullData: '0',
        drawCrossLine: '1',
        lineThickness: '2',
        anchorRadius: '5',
        showBorder: '0',
        showValues: '0',
        numberSuffix: likert ? '%' : '',
        yAxisMinValue: likert ? '0' : '-100',
        yAxisMaxValue: '100',
        labelFontColor: '#667085',
        valueFontColor: '#475467',
        legendPosition: 'bottom',
      },
      categories: [
        {
          category: aplicacoes.map((aplicacao) => ({
            label: format(aplicacao.data, currentDisplay, {
              locale: currentLocale,
            }),
          })),
        },
      ],
      dataset: series.map((serie) => ({
        seriesname: serie.label,
        color: serie.color,
        data: aplicacoes.map((aplicacao) => {
          const valor = obterValor(serie.obter(aplicacao));
          const selecionada =
            filtros?.formularioUsoId != null &&
            String(aplicacao.formularioUsoId) ===
              String(filtros.formularioUsoId);
          const dataFormatada = format(aplicacao.data, currentDisplay, {
            locale: currentLocale,
          });

          return {
            value: valor == null ? '' : String(valor),
            anchorBgColor: serie.color,
            anchorBorderColor: selecionada ? '#B54708' : '#FFFFFF',
            anchorBorderThickness: selecionada ? '3' : '1',
            anchorRadius: selecionada ? '8' : '5',
            ...(serie.percentual && valor != null
              ? {
                  toolText: `${dataFormatada}{br}${
                    serie.label
                  }: <b>${intl.formatNumber(valor, {
                    maximumFractionDigits: 1,
                  })}%</b>`,
                }
              : {}),
          };
        }),
      })),
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>
          {intl.formatMessage({
            id:
              tipo === 'nps'
                ? 'labelEvolucaoNpsAplicacoes'
                : 'labelEvolucaoLikertAplicacoes',
          })}
        </div>
        <EvolucaoFiltroAplicacoes filtro={filtroEvolucao} />
      </div>
      <EvolucaoFiltroChips filtro={filtroEvolucao} />
      {!filtroEvolucao.periodoInvalido &&
        !filtroEvolucao.semAplicacoesSelecionadas && (
          <LoadingContainer isLoading={isLoading}>
            {aplicacoes.length ? (
              <Grafico {...chartConfig} />
            ) : (
              <div style={styles.empty}>
                {intl.formatMessage({ id: 'msgEvolucaoAplicacoesSemDados' })}
              </div>
            )}
          </LoadingContainer>
        )}
    </div>
  );
}

export default EvolucaoAplicacoes;
